import React, { useState } from 'react';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import { Box } from '@material-ui/core';
import type { OrderItemJobTypeKey } from '@this/src/domain/order_item/order_item_job_type';
import { OrderItemJobType, OrderItemJobTypeKeys } from '@this/src/domain/order_item/order_item_job_type';
import type { OrderItemStatusKey } from '@this/domain/order_item/order_item_status';
import { OrderItemStatus, OrderItemStatusAvailable2 } from '@this/domain/order_item/order_item_status';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@this/shared/ui/feedbacks/modal';
import { Fetcher, HTTPError } from '@this/src/util';

interface Props {
  jobType?: OrderItemJobTypeKey;
  status: OrderItemStatusKey;
  onJobTypeChange: (value?: OrderItemJobTypeKey) => void;
  onStatusChange: (value: OrderItemStatusKey) => void;
  showLabel: boolean;
  style?: React.CSSProperties;
  classNameForModifiedField?: (...path: (string | number)[]) => string;
  fetchData: () => void; // POST /ordersでは新しいOrderItemが作成されるため、保存成功後に最新の情報を取得し、データの不整合を防ぐ
  tripId: number;
  traceId: number;
  updatedAt: string;
  needsShareholderTicketCheck?: boolean;
}

const OrderItemStatusForm = observer(
  ({
    jobType,
    status,
    onJobTypeChange,
    onStatusChange,
    showLabel,
    style,
    classNameForModifiedField,
    fetchData,
    tripId,
    traceId,
    updatedAt,
    needsShareholderTicketCheck
  }: Props) => {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState('');
    const [initialJobType, setInitialJobType] = useState(jobType);
    const [initialStatus, setInitialStatus] = useState(status);
    const [showShareholderAlert, setShowShareholderAlert] = useState(false);

    const executeSave = () => {
      setSaving(true);

      const params = {
        trip_id: tripId,
        simple_update: true, // 単体のorder_itemのjob_typeやstatusの更新のため
        order_items: [
          {
            trace_id: traceId,
            job_type: jobType,
            status
          }
        ],
        order_items_updated_at: [
          {
            trace_id: traceId,
            updated_at: updatedAt
          }
        ]
      };

      Fetcher.post('/orders', params)
        .then(() => {
          // 保存に成功した場合、initialJobType/initialStatusを変更しておく
          setInitialJobType(jobType);
          setInitialStatus(status);
          setSaving(false);
          setEditing(false);
          fetchData();
        })
        .catch(e => {
          if (e instanceof HTTPError && e.response?.data.errors) {
            setAlert(e.response.data.errors);
          }
          setSaving(false);
        });
    };

    const handleSave = () => {
      if (status === 12 && needsShareholderTicketCheck) {
        setShowShareholderAlert(true);
        return;
      }
      executeSave();
    };

    const handleCancelClick = () => {
      onJobTypeChange(initialJobType);
      onStatusChange(initialStatus);
      setEditing(false);
    };

    return (
      <>
        <Box display="flex" alignItems="center" style={style}>
          {showLabel && (
            <span
              style={{ marginRight: '5px' }}
              className={
                classNameForModifiedField
                  ? `${classNameForModifiedField('status')} ${classNameForModifiedField('jobType')}`
                  : ''
              }
            >
              ステータス:
            </span>
          )}
          {saving ? (
            <>保存中...</>
          ) : editing ? (
            <Box>
              <select
                onChange={e => onJobTypeChange(e.target.value as OrderItemJobTypeKey)}
                value={jobType}
                style={{ marginBottom: '5px' }}
              >
                {OrderItemJobTypeKeys.map(key => (
                  <option key={key} value={key}>
                    {OrderItemJobType[key]}
                  </option>
                ))}
              </select>
              <select
                onChange={e => onStatusChange(parseInt(e.target.value, 10) as OrderItemStatusKey)}
                value={status}
                style={{ marginBottom: '5px' }}
              >
                {OrderItemStatusAvailable2.map(key => (
                  <option key={key} value={key}>
                    {OrderItemStatus[key]}
                  </option>
                ))}
              </select>
              <Box display="flex">
                <Button size="small" onClick={() => handleSave()}>
                  保存
                </Button>
                <Button size="small" color="sub" onClick={() => handleCancelClick()} style={{ marginLeft: '5px' }}>
                  キャンセル
                </Button>
              </Box>
              {alert && <AlertMessage>{alert}</AlertMessage>}
            </Box>
          ) : jobType ? (
            <Box
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (!jobType) {
                  onJobTypeChange(0);
                }
                setEditing(true);
              }}
            >
              {OrderItemJobType[jobType]}-{OrderItemStatus[status]}
            </Box>
          ) : null}
        </Box>
        <Modal open={showShareholderAlert} onClose={() => setShowShareholderAlert(false)}>
          <ModalHeader>確認</ModalHeader>
          <ModalBody>株主優待券が紐づいていませんが、問題ありませんか？</ModalBody>
          <ModalFooter>
            <Button color="sub" onClick={() => setShowShareholderAlert(false)}>
              キャンセル
            </Button>
            <Button
              color="primary"
              onClick={() => {
                setShowShareholderAlert(false);
                executeSave();
              }}
            >
              OK
            </Button>
          </ModalFooter>
        </Modal>
      </>
    );
  }
);

const AlertMessage = styled.div`
  color: red;
  white-space: pre-line;
`;

export default OrderItemStatusForm;
